import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomToast from "../misc/CustomToast";
import { getAuthHeader } from "../../utils/authHeader";
import NoItems from "../misc/NoItems";
import LoadingScreen from "../../screens/LoadingScreen";
import ListingDisplay from "./ListingDisplay";
import { AllListings } from "../../types/allListings";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import useAppSelector from "../../hooks/useAppSelector";
import WelcomeListing from "./WelcomeListing";
import { SocketProvider, useSocket } from "../../context/SocketContext";
import axios from "axios";
import MLSWelcome from "./MLSWelcome";
import MlsListingReviewModal from "./MlsListingReviewModal";
import MlsSetupReviewModal from "./MlsSetupReviewModal";
import MlsReviewBanner from "./MlsReviewBanner";
import MlsFeedStatusBanner from "./MlsFeedStatusBanner";
import { useMlsFeedStatus } from "../../hooks/useMlsFeedStatus";
import { getMlsCandidates, refreshMlsCandidates } from "./mlsCandidatesApi";

interface UserListingsProps {
    mlsApproved: boolean | null;
    mlsWelcome: boolean;
}

// Lives inside <SocketProvider> so it can subscribe. When the ~15-min ingest leaves
// listings Pending for review it emits `mlsReview:available`; we refresh the count so the
// "Review" button/banner appears live without a reload. We never auto-open the modal —
// the user opens it from the button.
const ReviewSocketSync: React.FC<{ onReviewAvailable: () => void }> = ({
    onReviewAvailable,
}) => {
    const { socket } = useSocket();
    useEffect(() => {
        if (!socket) return;
        // mlsReview:available → the poll left listings to review.
        // listing:* → listings were added/removed; auto-add flips candidates to "Added"
        // (shrinking the pending set) and the media tasks then stream listing:updated, so
        // re-fetch the count to clear a stale "N to review" banner without a page reload.
        //
        // Throttle (trailing) the refresh: an auto-add run emits a burst of listing:* events,
        // and firing a fetch per event both spams the API and risks latching a stale mid-run
        // count. One refresh per window settles on the final count; refreshReviewCounts's
        // seq-guard drops any out-of-order response. Throttle (not debounce) so a steady
        // stream of media-progress events can't starve the refresh.
        const THROTTLE_MS = 1500;
        let last = 0;
        let trailing: ReturnType<typeof setTimeout> | null = null;
        const run = (): void => {
            last = Date.now();
            onReviewAvailable();
        };
        const handler = (): void => {
            const elapsed = Date.now() - last;
            if (elapsed >= THROTTLE_MS) {
                run();
            } else if (!trailing) {
                trailing = setTimeout(() => {
                    trailing = null;
                    run();
                }, THROTTLE_MS - elapsed);
            }
        };
        socket.on("mlsReview:available", handler);
        socket.on("listing:created", handler);
        socket.on("listing:updated", handler);
        socket.on("listing:deleted", handler);
        return () => {
            if (trailing) clearTimeout(trailing);
            socket.off("mlsReview:available", handler);
            socket.off("listing:created", handler);
            socket.off("listing:updated", handler);
            socket.off("listing:deleted", handler);
        };
    }, [socket, onReviewAvailable]);
    return null;
};

const UserListings: React.FC<UserListingsProps> = () => {
    // Read directly from Redux to ensure impersonation state is current
    const mlsApproved = useAppSelector((state) => state.auth.mlsApproved);
    // MB-1611: polled once a minute while this page is open, for MLS users only;
    // null until the first answer.
    const feedStatus = useMlsFeedStatus(Boolean(mlsApproved));
    const mlsWelcome = useAppSelector((state) => state.auth.user?.mlsWelcome || false);
    const listingsCreated = useSelector(
        (state: RootState) => state.auth.listingCreated
    );
    const isImpersonating = useAppSelector((state) => state.auth.isImpersonating);
    // MLS auto-sync is configured once the user has saved their agent ID. The review
    // banner (Refresh + Review + manage-details) is shown whenever this is set, so the
    // review modal stays reachable even when nothing is currently pending.
    const hasMlsAgentId = useAppSelector((state) =>
        Boolean(state.auth.user?.listAgentMlsId?.trim())
    );

    const navigate = useNavigate();
    const location = useLocation();

    const [listings, setListings] = useState<AllListings[]>([]);

    const [listingsActiveLimit, setListingsActiveLimit] = useState<number>(0);
    const [listingsLimit, setListingsLimit] = useState<number>(0);
    const [listingsWarn, setListingsWarn] = useState<number>(0);

    const [loading, setLoading] = useState(true);

    const hasFetched = useRef(false); // Ref to prevent duplicate fetches

    const [userStateUpdated, setUserStateUpdated] = useState(true);

    const [canceledMlsWelcome, setCanceledMlsWelcome] = useState(false);

    // MB-1313 select-or-add review state.
    const [reviewOpen, setReviewOpen] = useState(false);
    // MB-1357 first-time Save & Auto-Sync: the create flow hands us here with
    // `location.state.openMlsReview === "setup"` so we auto-open the review modal in
    // "setup mode" (un-picked listings are excluded on Import / on close).
    const [reviewSetupMode, setReviewSetupMode] = useState(false);
    // Drives the spinning refresh icon while a manual banner-refresh discovery runs.
    const [bannerRefreshing, setBannerRefreshing] = useState(false);
    // Lets the user dismiss the full-screen "No Listings Found" overlay to reach
    // the review banner underneath when they have pending imports but no listings.
    const [noListingsDismissed, setNoListingsDismissed] = useState(false);
    const [pendingReviewCount, setPendingReviewCount] = useState(0);
    const [archivedReviewCount, setArchivedReviewCount] = useState(0);
    // Tags each refreshReviewCounts run so only the latest applies (see below).
    const reviewFetchSeq = useRef(0);

    const checkUser = useCallback(async () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const headers = await getAuthHeader();

        try {
            const response = await axios.post(
                `${backendUrl}/listing/check-user`,
                {
                    mlsApproved,
                    mlsWelcome,
                },
                { headers }
            );

            if (response.status === 200) {
                setUserStateUpdated(response.data);
            }
        } catch (error) {
            console.error("Error checking user:", error);
        }
    }, [mlsApproved, mlsWelcome]);

    useEffect(() => {
        checkUser();
    }, [checkUser]);

    useEffect(() => {
        if (!userStateUpdated && !isImpersonating) {
            window.location.reload();
        }
    }, [userStateUpdated, isImpersonating]);

    const fetchListings = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            if (!backendUrl) {
                throw new Error("REACT_APP_BACKEND_URL is not defined.");
            }

            const response = await fetch(`${backendUrl}/listing`, { headers });

            if (!response.ok) {
                throw new Error(
                    "Failed to fetch listings. Please try again later."
                );
            }

            const responseBody = response.body;
            if (!responseBody) {
                throw new Error("Response body is null.");
            }

            const reader = responseBody.getReader();
            const decoder = new TextDecoder();
            let { value, done } = await reader.read();
            let buffer = "";
            const fetchedListings: AllListings[] = [];

            let isFirstLine = true;
            let isSecondLine = true;
            let isThirdLine = true;

            while (!done) {
                buffer += decoder.decode(value, { stream: true });
                let lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (let line of lines) {
                    if (line.trim()) {
                        if (isFirstLine) {
                            setListingsActiveLimit(Number(line.trim()));
                            isFirstLine = false;
                            continue;
                        } else if (isSecondLine) {
                            setListingsWarn(Number(line.trim()));
                            isSecondLine = false;
                            continue;
                        } else if (isThirdLine) {
                            setListingsLimit(Number(line.trim()));
                            isThirdLine = false;
                            continue;
                        }

                        try {
                            const listing = JSON.parse(line);
                            fetchedListings.push(listing);
                        } catch (parseError) {
                            console.error(
                                "Error parsing listing:",
                                parseError,
                                "Line:",
                                line
                            );
                        }
                    }
                }

                const result = await reader.read();
                value = result.value;
                done = result.done;
            }
            // Process any remaining data
            if (buffer.trim()) {
                try {
                    const listing = JSON.parse(buffer);
                    fetchedListings.push(listing);
                } catch (parseError) {
                    console.error(
                        "Error parsing final listing:",
                        parseError,
                        "Buffer:",
                        buffer
                    );
                }
            }

            // Optionally remove duplicates based on unique identifier
            const uniqueListings = fetchedListings.reduce(
                (acc: AllListings[], current) => {
                    const exists = acc.some((item) => item.id === current.id);
                    if (!exists) acc.push(current);
                    return acc;
                },
                []
            );

            setListings(uniqueListings);
        } catch (error) {
            toast.error(
                <CustomToast
                    message="Failed to fetch listings. Please try again later."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            console.error("Error fetching listings:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasFetched.current) return; // Prevent duplicate fetches
        hasFetched.current = true;
        setLoading(true);
        fetchListings();
    }, [fetchListings]);

    // Candidates only exist for MLS-approved users. Pending drives the "ready to review" nag;
    // the excluded (Discarded) count only powers a quiet "Manage MLS sync" entry point so the
    // Excluded tab stays reachable when nothing is Pending — excluded listings never nag.
    const refreshReviewCounts = useCallback(async () => {
        if (!mlsApproved) return;
        // Auto-add flips candidates Pending→Added one-by-one while their media tasks stream
        // listing:* events, so this runs rapidly and its fetches can resolve out of order.
        // Tag each run and apply only the latest, so a slow older response can't latch a stale
        // mid-run count (e.g. "1 to review" when everything has actually been added).
        const seq = ++reviewFetchSeq.current;
        try {
            const [pendingRes, archivedRes] = await Promise.all([
                getMlsCandidates("Pending"),
                getMlsCandidates("Discarded"),
            ]);
            if (seq !== reviewFetchSeq.current) return;
            setPendingReviewCount(pendingRes.candidates.length);
            setArchivedReviewCount(archivedRes.candidates.length);
        } catch {
            // Non-critical background refresh — keep the current counts if it fails.
        }
    }, [mlsApproved]);

    useEffect(() => {
        refreshReviewCounts();
    }, [refreshReviewCounts]);

    // Auto-open the review modal in setup mode when we arrive from a first-time
    // Save & Auto-Sync. Clear the router state (replace) afterward so a refresh or
    // back-nav doesn't re-open it.
    useEffect(() => {
        const state = location.state as { openMlsReview?: string } | null;
        if (state?.openMlsReview === "setup") {
            setReviewSetupMode(true);
            setReviewOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const handleReviewChanged = useCallback(() => {
        fetchListings();
        refreshReviewCounts();
    }, [fetchListings, refreshReviewCounts]);

    // Manual "Refresh" from the banner: re-run a per-user, review-only discovery (never
    // auto-adds), then refresh listings + review counts. Guarded so overlapping clicks and
    // failures are handled gracefully.
    const handleRefreshDiscovery = useCallback(async () => {
        setBannerRefreshing(true);
        try {
            await refreshMlsCandidates();
            await Promise.all([fetchListings(), refreshReviewCounts()]);
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't refresh MLS listings. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setBannerRefreshing(false);
        }
    }, [fetchListings, refreshReviewCounts]);

    const createListingHandler = () => {
        navigate("/create-listing");
    };

    const welcome: boolean = sessionStorage.getItem("welcome") === "true";
    return (
        <SocketProvider>
            <ReviewSocketSync onReviewAvailable={refreshReviewCounts} />
            <div className="relative w-full h-full">
                {loading && <LoadingScreen />}
                {/* MB-1611: visible while the MLS feed is down (detected or forced). */}
                {!loading && <MlsFeedStatusBanner status={feedStatus} />}
                {/* While the "No Listings Found" overlay is up, ListingDisplay isn't rendered,
                    so show the review prompt at the top; once the overlay is dismissed (or there
                    are listings) it sits inline with ListingDisplay's header instead. */}
                {!loading &&
                    mlsApproved &&
                    hasMlsAgentId &&
                    listings.length === 0 &&
                    !noListingsDismissed && (
                        <div className="mb-4 flex justify-center">
                            <MlsReviewBanner
                                pendingCount={pendingReviewCount}
                                archivedCount={archivedReviewCount}
                                onReview={() => setReviewOpen(true)}
                                onRefresh={handleRefreshDiscovery}
                                refreshing={bannerRefreshing}
                            />
                        </div>
                    )}
                {mlsWelcome && mlsApproved && !canceledMlsWelcome && !listingsCreated && (
                    <MLSWelcome
                        setCancelMlsWelcome={setCanceledMlsWelcome}
                        listingCount={listings?.length || 0}
                    />
                )}
                {!loading &&
                listings.length === 0 &&
                listingsCreated &&
                !noListingsDismissed ? (
                    <NoItems
                        title="No Listings Found"
                        description="You have not created any listings yet."
                        descriptionTwo="Click the button below to create a new listing."
                        buttonText="Create Listing"
                        clickHandler={createListingHandler}
                        close={
                            mlsApproved && hasMlsAgentId
                                ? () => setNoListingsDismissed(true)
                                : undefined
                        }
                    />
                ) : !loading && listings.length === 0 && !mlsApproved ? (
                    <WelcomeListing welcome={welcome} />
                ) : null}
                {/* Also rendered on an empty list once the overlay is dismissed: it owns the
                    "My Listings" header and the Add Listing button, without which a dismissing
                    user has no way back to the create flow. The grid simply renders empty. */}
                {!loading && (listings.length > 0 || noListingsDismissed) && (
                    <ListingDisplay
                        listings={listings}
                        mlsApproved={mlsApproved || false}
                        fetchListings={fetchListings}
                        listingsActiveLimit={listingsActiveLimit}
                        listingsWarn={listingsWarn}
                        listingsLimit={listingsLimit}
                        reviewCount={pendingReviewCount}
                        archivedReviewCount={archivedReviewCount}
                        onReview={() => setReviewOpen(true)}
                        hasMlsAgentId={hasMlsAgentId}
                        onRefreshReview={handleRefreshDiscovery}
                        reviewRefreshing={bannerRefreshing}
                    />
                )}
                {/* Two distinct dialogs: the first-run setup review (opened from the create
                    flow) and the banner-opened review. Kept separate so their state never mixes. */}
                <MlsSetupReviewModal
                    open={reviewOpen && reviewSetupMode}
                    onOpenChange={(open) => {
                        setReviewOpen(open);
                        if (!open) setReviewSetupMode(false);
                    }}
                    onChanged={handleReviewChanged}
                    listings={listings}
                />
                <MlsListingReviewModal
                    open={reviewOpen && !reviewSetupMode}
                    onOpenChange={setReviewOpen}
                    onChanged={handleReviewChanged}
                    listings={listings}
                    initialTab={
                        pendingReviewCount > 0
                            ? "Pending"
                            : archivedReviewCount > 0
                            ? "Discarded"
                            : "MyListings"
                    }
                />
            </div>
        </SocketProvider>
    );
};

export default UserListings;
